/**
 * Banners de tienda — admin CRUD para los banners del home público.
 *
 * Dos slots:
 *   • hero  — carrusel principal del `/` (StorefrontHome)
 *   • promo — banners horizontales intercalados entre secciones del home
 *
 * Cada banner tiene:
 *   - imagen de fondo (upload Cloudinary)
 *   - título / subtítulo
 *   - texto de botón (CTA label)
 *   - link del botón: selector guiado (Categoría / Uso / Presupuesto / Ofertas)
 *     o URL personalizada en modo avanzado
 *   - color de acento (gradiente overlay)
 *   - activo sí/no + orden (↑/↓)
 *
 * Ruta: /banners
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { uploadPhoto } from "@/lib/uploadPhoto";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, PencilSimple, Trash, ArrowUp, ArrowDown, ImageSquare, Link as LinkIcon,
  Eye, EyeSlash, ArrowSquareOut, ArrowClockwise,
} from "@phosphor-icons/react";

const SLOT_META = {
  hero: {
    label: "Hero · carrusel principal",
    hint: "Se muestra en la parte superior del home, rota automáticamente cada 6 s.",
    aspect: "aspect-[21/9]",
    max: 5,
  },
  promo: {
    label: "Banners promocionales",
    hint: "Franjas horizontales que aparecen entre secciones del home.",
    aspect: "aspect-[16/5]",
    max: 3,
  },
};

const BUDGETS = [
  { key: "b1", label: "Menos de $2,500" },
  { key: "b2", label: "$2,500 – $4,000" },
  { key: "b3", label: "$4,000 – $6,000" },
  { key: "b4", label: "Más de $6,000" },
];

const ACCENT_PRESETS = [
  "#002FA7", "#2563eb", "#7C3AED", "#059669",
  "#E0000F", "#E91E63", "#F59E0B", "#0F172A",
];

const emptyBanner = (slot = "hero") => ({
  slot,
  image_url: "",
  title: "",
  subtitle: "",
  cta_label: "",
  link_type: "none",
  link_value: "",
  cta_href: "",
  accent_color: "#002FA7",
  active: true,
  sort_order: 0,
});

export default function Banners() {
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [useCases, setUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // banner object being edited (or null)
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, u, c] = await Promise.all([
        api.get("/storefront/banners"),
        api.get("/products/use-cases").catch(() => ({ data: [] })),
        api.get("/categories").catch(() => ({ data: [] })),
      ]);
      setBanners(b.data.banners || []);
      setUseCases(u.data || []);
      setCategories(c.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando banners");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const bySlot = useMemo(() => {
    const map = { hero: [], promo: [] };
    for (const b of banners) {
      (map[b.slot] || (map[b.slot] = [])).push(b);
    }
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    return map;
  }, [banners]);

  const moveBanner = async (slot, id, dir) => {
    const arr = [...(bySlot[slot] || [])];
    const idx = arr.findIndex((b) => b.id === id);
    if (idx < 0) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    const order = arr.map((b) => b.id);
    try {
      await api.post("/storefront/banners/reorder", { slot, order });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reordenar");
    }
  };

  const toggleActive = async (b) => {
    try {
      await api.put(`/storefront/banners/${b.id}`, { ...b, active: !b.active });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/storefront/banners/${deleteId}`);
      toast.success("Banner eliminado");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const startNew = (slot) => setEditing(emptyBanner(slot));
  const startEdit = (b) => setEditing({ ...b });

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-8" data-testid="banners-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#002FA7]">Tienda pública</div>
          <h1 className="font-heading text-3xl font-extrabold">Banners de tienda</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Administra las imágenes y botones que aparecen en el home público (<code className="text-[11px] bg-zinc-100 px-1 rounded">/</code>).
            Los cambios se reflejan al instante al recargar la página.
          </p>
        </div>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-[#002FA7] hover:underline"
          data-testid="banners-open-preview"
        >
          Ver home <ArrowSquareOut size={14} weight="bold" />
        </a>
      </header>

      {loading ? (
        <div className="text-sm text-zinc-500">Cargando…</div>
      ) : (
        <>
          {Object.entries(SLOT_META).map(([slot, meta]) => {
            const list = bySlot[slot] || [];
            const canAdd = list.length < meta.max;
            return (
              <section key={slot} data-testid={`banners-section-${slot}`}>
                <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
                  <div>
                    <h2 className="font-heading text-lg font-extrabold flex items-center gap-2">
                      {meta.label}
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                        {list.length}/{meta.max}
                      </span>
                    </h2>
                    <p className="text-xs text-zinc-500 mt-0.5">{meta.hint}</p>
                  </div>
                  <Button
                    onClick={() => startNew(slot)}
                    disabled={!canAdd}
                    size="sm"
                    className="bg-[#002FA7] hover:bg-[#0043c2]"
                    data-testid={`banners-add-${slot}`}
                  >
                    <Plus size={14} weight="bold" /> Nuevo banner
                  </Button>
                </div>

                {list.length === 0 ? (
                  <div className="border-2 border-dashed border-zinc-200 rounded-xl p-10 text-center bg-zinc-50">
                    <ImageSquare size={36} className="mx-auto text-zinc-300" />
                    <p className="text-sm text-zinc-500 mt-2">Aún no hay banners en este slot.</p>
                    <p className="text-xs text-zinc-400 mt-1">Haz clic en "Nuevo banner" para agregar el primero.</p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {list.map((b, i) => (
                      <BannerCard
                        key={b.id}
                        banner={b}
                        aspect={meta.aspect}
                        canUp={i > 0}
                        canDown={i < list.length - 1}
                        onEdit={() => startEdit(b)}
                        onDelete={() => setDeleteId(b.id)}
                        onToggle={() => toggleActive(b)}
                        onUp={() => moveBanner(slot, b.id, -1)}
                        onDown={() => moveBanner(slot, b.id, 1)}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </>
      )}

      {editing && (
        <BannerEditor
          initial={editing}
          categories={categories}
          useCases={useCases}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <Dialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>¿Eliminar banner?</DialogTitle>
            <DialogDescription>Esta acción no se puede deshacer.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="banners-confirm-delete">
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BannerCard({ banner, aspect, canUp, canDown, onEdit, onDelete, onToggle, onUp, onDown }) {
  const inactive = !banner.active;
  return (
    <li
      className={`bg-white border rounded-xl overflow-hidden flex flex-col sm:flex-row transition-all ${
        inactive ? "border-zinc-200 opacity-60" : "border-zinc-300 hover:border-[#002FA7]/40 hover:shadow-md"
      }`}
      data-testid={`banner-card-${banner.id}`}
    >
      <div
        className={`sm:w-64 shrink-0 ${aspect} bg-zinc-100 relative overflow-hidden`}
        style={{ background: banner.image_url ? undefined : `linear-gradient(135deg, ${banner.accent_color} 0%, #001a5c 100%)` }}
      >
        {banner.image_url ? (
          <img src={banner.image_url} alt={banner.title || "banner"} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/60">
            <ImageSquare size={30} />
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-white shadow"
            style={{ backgroundColor: banner.accent_color }}
          >
            {banner.slot}
          </span>
          {inactive && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-zinc-700 text-white">
              Oculto
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 p-4 flex flex-col gap-1 min-w-0">
        <div className="font-heading font-extrabold text-base truncate">{banner.title || <span className="text-zinc-400 italic">Sin título</span>}</div>
        {banner.subtitle && <div className="text-xs text-zinc-500 line-clamp-1">{banner.subtitle}</div>}
        <div className="flex items-center gap-2 mt-1 text-[11px] text-zinc-500 flex-wrap">
          <LinkIcon size={12} weight="bold" />
          {banner.cta_href ? (
            <a href={banner.cta_href} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline truncate max-w-[280px]">
              {banner.cta_label ? `${banner.cta_label} → ` : ""}{banner.cta_href}
            </a>
          ) : (
            <span className="italic text-zinc-400">Sin link</span>
          )}
        </div>
      </div>
      <div className="p-3 border-t sm:border-t-0 sm:border-l border-zinc-100 flex sm:flex-col items-center justify-end gap-1 shrink-0">
        <Button variant="ghost" size="icon" title="Subir" onClick={onUp} disabled={!canUp} data-testid={`banner-up-${banner.id}`}>
          <ArrowUp size={14} weight="bold" />
        </Button>
        <Button variant="ghost" size="icon" title="Bajar" onClick={onDown} disabled={!canDown} data-testid={`banner-down-${banner.id}`}>
          <ArrowDown size={14} weight="bold" />
        </Button>
        <Button variant="ghost" size="icon" title={banner.active ? "Ocultar" : "Mostrar"} onClick={onToggle} data-testid={`banner-toggle-${banner.id}`}>
          {banner.active ? <Eye size={14} weight="bold" /> : <EyeSlash size={14} weight="bold" />}
        </Button>
        <Button variant="ghost" size="icon" title="Editar" onClick={onEdit} data-testid={`banner-edit-${banner.id}`}>
          <PencilSimple size={14} weight="bold" />
        </Button>
        <Button variant="ghost" size="icon" title="Eliminar" onClick={onDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`banner-delete-${banner.id}`}>
          <Trash size={14} weight="bold" />
        </Button>
      </div>
    </li>
  );
}

function BannerEditor({ initial, categories, useCases, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [advanced, setAdvanced] = useState(!!initial.id && initial.link_type === "custom");

  const isNew = !initial.id;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const previewHref = useMemo(() => {
    if (advanced) return (form.cta_href || "").trim();
    const val = (form.link_value || "").trim();
    switch (form.link_type) {
      case "category":
        return val ? `/catalogo?category=${encodeURIComponent(val)}` : "";
      case "use_case":
        return val ? `/catalogo?use_case=${encodeURIComponent(val)}` : "";
      case "budget": {
        const map = { b1: "min=0&max=2500", b2: "min=2500&max=4000", b3: "min=4000&max=6000", b4: "min=6000" };
        return map[val] ? `/catalogo?${map[val]}` : "";
      }
      case "offers": return "/catalogo?offers=1";
      case "custom": return val;
      default: return "";
    }
  }, [form.link_type, form.link_value, form.cta_href, advanced]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPhoto(file, { folder: "alendrapc/banners" });
      set({ image_url: url });
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(err.message || "Error subiendo imagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.image_url && !form.title) {
      toast.error("Agrega al menos una imagen o un título");
      return;
    }
    const payload = {
      ...form,
      cta_href: advanced ? (form.cta_href || "").trim() : "",
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/storefront/banners", payload);
        toast.success("Banner creado");
      } else {
        await api.put(`/storefront/banners/${initial.id}`, payload);
        toast.success("Banner actualizado");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nuevo banner" : "Editar banner"}</DialogTitle>
          <DialogDescription>Slot: <strong>{form.slot}</strong> · Se muestra en el home público.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-5">
          {/* Preview */}
          <div
            className="relative overflow-hidden rounded-xl h-40 flex items-center px-6 text-white"
            style={{
              background: form.image_url
                ? `linear-gradient(135deg, ${form.accent_color}CC 0%, transparent 55%), url(${form.image_url}) center/cover`
                : `linear-gradient(135deg, ${form.accent_color} 0%, #001a5c 60%)`,
            }}
          >
            <div className="relative z-10 max-w-md">
              {form.title && <div className="font-heading text-xl font-extrabold leading-tight">{form.title}</div>}
              {form.subtitle && <div className="text-xs opacity-90 mt-1 line-clamp-2">{form.subtitle}</div>}
              {form.cta_label && previewHref && (
                <div className="mt-3 inline-flex items-center gap-1 bg-white text-[#002FA7] font-bold text-[11px] px-3 py-1.5 rounded-full">
                  {form.cta_label}
                </div>
              )}
            </div>
          </div>

          {/* Image upload */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Imagen de fondo</Label>
            <div className="mt-2 flex items-center gap-3">
              {form.image_url ? (
                <img src={form.image_url} alt="banner" className="w-32 h-20 object-cover rounded-md border border-zinc-200" />
              ) : (
                <div className="w-32 h-20 bg-zinc-100 rounded-md flex items-center justify-center">
                  <ImageSquare size={22} className="text-zinc-300" />
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <label className="inline-flex items-center gap-1.5 cursor-pointer bg-white border border-zinc-200 hover:border-[#002FA7] text-sm rounded-md px-3 py-1.5 font-semibold">
                  {uploading ? <ArrowClockwise size={13} className="animate-spin" /> : <Plus size={13} weight="bold" />}
                  {uploading ? "Subiendo…" : "Subir imagen"}
                  <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="banner-file-input" />
                </label>
                {form.image_url && (
                  <button type="button" onClick={() => set({ image_url: "" })} className="text-xs text-red-600 hover:underline">
                    Quitar imagen
                  </button>
                )}
                <div className="text-[11px] text-zinc-400">Recomendado: 1600×700 (hero) o 1600×500 (promo)</div>
              </div>
            </div>
          </div>

          {/* Texto */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Ej. Gaming sin compromisos" data-testid="banner-title" />
            </div>
            <div>
              <Label>Texto del botón (opcional)</Label>
              <Input value={form.cta_label} onChange={(e) => set({ cta_label: e.target.value })} placeholder="Ej. Ver gaming" data-testid="banner-cta-label" />
            </div>
          </div>
          <div>
            <Label>Subtítulo</Label>
            <Textarea
              value={form.subtitle}
              onChange={(e) => set({ subtitle: e.target.value })}
              rows={2}
              placeholder="Copy corto que refuerza el título (máx. 2 líneas)"
              data-testid="banner-subtitle"
            />
          </div>

          {/* Link selector */}
          <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50/60">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">A dónde lleva el botón</Label>
              <button
                type="button"
                onClick={() => setAdvanced((v) => !v)}
                className="text-[11px] text-[#002FA7] hover:underline"
                data-testid="banner-advanced-toggle"
              >
                {advanced ? "Usar selector guiado" : "Modo avanzado (URL libre)"}
              </button>
            </div>

            {!advanced ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "none",     label: "Sin link" },
                    { key: "category", label: "Categoría" },
                    { key: "use_case", label: "Uso" },
                    { key: "budget",   label: "Presupuesto" },
                    { key: "offers",   label: "Ofertas" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => set({ link_type: opt.key, link_value: "" })}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        form.link_type === opt.key
                          ? "bg-[#002FA7] text-white border-[#002FA7]"
                          : "bg-white text-zinc-600 border-zinc-200 hover:border-[#002FA7]/40"
                      }`}
                      data-testid={`banner-linktype-${opt.key}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {form.link_type === "category" && (
                  <select
                    value={form.link_value}
                    onChange={(e) => set({ link_value: e.target.value })}
                    className="w-full h-9 px-2 text-sm border border-zinc-300 rounded-md bg-white"
                    data-testid="banner-link-category"
                  >
                    <option value="">Elige una categoría…</option>
                    {(categories || []).map((c, i) => (
                      <option key={c.id || c.name || i} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                )}
                {form.link_type === "use_case" && (
                  <select
                    value={form.link_value}
                    onChange={(e) => set({ link_value: e.target.value })}
                    className="w-full h-9 px-2 text-sm border border-zinc-300 rounded-md bg-white"
                    data-testid="banner-link-usecase"
                  >
                    <option value="">Elige un uso…</option>
                    {(useCases || []).map((u) => (
                      <option key={u.key} value={u.key}>{u.label}</option>
                    ))}
                  </select>
                )}
                {form.link_type === "budget" && (
                  <select
                    value={form.link_value}
                    onChange={(e) => set({ link_value: e.target.value })}
                    className="w-full h-9 px-2 text-sm border border-zinc-300 rounded-md bg-white"
                    data-testid="banner-link-budget"
                  >
                    <option value="">Elige un rango…</option>
                    {BUDGETS.map((b) => (
                      <option key={b.key} value={b.key}>{b.label}</option>
                    ))}
                  </select>
                )}
                {form.link_type === "offers" && (
                  <div className="text-xs text-zinc-500">→ Enviará al catálogo mostrando solo productos con descuento activo.</div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  value={form.cta_href}
                  onChange={(e) => set({ cta_href: e.target.value, link_type: "custom", link_value: e.target.value })}
                  placeholder="Ej. /catalogo?category=Gaming&brand=ASUS  o  https://…"
                  data-testid="banner-link-custom"
                />
                <div className="text-[11px] text-zinc-500">
                  Acepta rutas internas (empiezan con <code>/</code>) o URLs completas <code>https://…</code>.
                </div>
              </div>
            )}
            {previewHref && (
              <div className="mt-3 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded px-2 py-1.5 truncate">
                <span className="text-zinc-400 uppercase tracking-widest font-mono mr-1.5">Vista previa:</span>
                <code className="text-[#002FA7]">{previewHref}</code>
              </div>
            )}
          </div>

          {/* Accent color + active */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Color de acento</Label>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {ACCENT_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set({ accent_color: c })}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      form.accent_color === c ? "border-zinc-900 scale-110" : "border-white shadow"
                    }`}
                    style={{ backgroundColor: c }}
                    aria-label={c}
                  />
                ))}
                <input
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => set({ accent_color: e.target.value })}
                  className="w-8 h-8 border border-zinc-200 rounded-full cursor-pointer"
                  data-testid="banner-accent-color"
                />
              </div>
            </div>
            <div className="flex flex-col justify-end">
              <div className="flex items-center gap-3 bg-white border border-zinc-200 rounded-md px-3 py-2">
                <Switch checked={form.active} onCheckedChange={(v) => set({ active: v })} data-testid="banner-active-switch" />
                <div>
                  <div className="text-sm font-semibold">Visible en el home</div>
                  <div className="text-[11px] text-zinc-500">Ocúltalo para editarlo sin publicarlo.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving || uploading} className="bg-[#002FA7] hover:bg-[#0043c2]" data-testid="banner-save">
            {saving ? "Guardando…" : isNew ? "Crear banner" : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
